import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { ProjectDTO } from "@app-factory/shared";
import { projectTone } from "@/design/project-visual";
import { toneColor, withAlpha } from "@/design/primitives";
import { cs } from "@/i18n/cs";

const SIZE = 640;
const CENTER = SIZE / 2;
const CORE_R = 86;
const MIN_R = 130;
const MAX_R = 268;

/**
 * Orbitální mapa portfolia (dle /Design, směr „Orbital Core"):
 * vyšší Attention Score = blíž jádru, velikost i barva tečky dle stavu.
 */
export function OrbitMap({
  projects,
  activitySummary,
}: {
  projects: ProjectDTO[];
  activitySummary: { activeSessions: number; tasksToday: number };
}) {
  const navigate = useNavigate();

  const placed = useMemo(() => {
    // stabilní rozmístění: úhel dle pořadí (zlatý úhel ať se neshlukují)
    return projects.map((p, i) => {
      const angle = (i * 137.5 * Math.PI) / 180 - Math.PI / 2;
      const r = MAX_R - (Math.max(0, Math.min(100, p.attentionScore)) / 100) * (MAX_R - MIN_R);
      return {
        project: p,
        x: CENTER + r * Math.cos(angle),
        y: CENTER + r * Math.sin(angle),
        dot: 6 + (p.attentionScore / 100) * 8,
        tone: projectTone(p),
      };
    });
  }, [projects]);

  // procentuální souřadnice, ať mapa škáluje na šířku kontejneru (tablet/mobil)
  const pct = (v: number) => `${(v / SIZE) * 100}%`;

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[640px]">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 h-full w-full" aria-hidden>
        {/* orbity */}
        {[CORE_R + 34, (CORE_R + MAX_R) / 2 + 20, MAX_R + 6].map((r, i) => (
          <circle
            key={i}
            cx={CENTER}
            cy={CENTER}
            r={r}
            fill="none"
            stroke="color-mix(in srgb, var(--accent) 10%, transparent)"
            strokeDasharray={i === 2 ? "3 7" : undefined}
          />
        ))}
        {/* jemné výseče */}
        {placed.map(({ x, y, tone }, i) => (
          <line
            key={i}
            x1={CENTER}
            y1={CENTER}
            x2={x}
            y2={y}
            stroke={withAlpha(tone, 0.05)}
          />
        ))}
        {/* core kruh */}
        <circle cx={CENTER} cy={CENTER} r={CORE_R} fill="none" stroke="color-mix(in srgb, var(--accent) 30%, transparent)" />
        <circle cx={CENTER} cy={CENTER} r={CORE_R} fill="color-mix(in srgb, var(--accent) 2%, transparent)" />
      </svg>

      {/* core obsah */}
      <div
        className="absolute flex flex-col items-center justify-center text-center"
        style={{ left: pct(CENTER - CORE_R), top: pct(CENTER - CORE_R), width: pct(CORE_R * 2), height: pct(CORE_R * 2) }}
      >
        <div className="af-label-dim">{cs.commandCenter.factoryCore}</div>
        <div className="af-display text-[34px] font-semibold" style={{ color: "var(--accent)" }}>
          {activitySummary.activeSessions}
        </div>
        <div className="af-label-dim">{cs.commandCenter.activeSessions}</div>
        <div className="mt-1 text-[11px]" style={{ color: "var(--positive)" }}>
          {cs.commandCenter.tasksToday(activitySummary.tasksToday)}
        </div>
      </div>

      {/* projekty */}
      {placed.map(({ project, x, y, dot, tone }) => (
        <button
          key={project.id}
          type="button"
          className="group absolute flex cursor-pointer flex-col items-center"
          style={{ left: pct(x), top: pct(y), transform: "translate(-50%, -50%)" }}
          onClick={() => void navigate({ to: "/projects/$slug", params: { slug: project.slug } })}
          data-testid={`orbit-${project.slug}`}
        >
          <span
            style={{
              width: dot,
              height: dot,
              borderRadius: dot,
              background: toneColor[tone],
              boxShadow: `0 0 ${dot * 1.6}px ${withAlpha(tone, 0.7)}`,
            }}
            className={project.attentionScore >= 90 ? "af-animate-pulse" : undefined}
          />
          <span
            className="af-display mt-1.5 text-[12px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap"
            style={{ color: "var(--text-primary)" }}
          >
            {project.name}
          </span>
          <span className="text-[11px] tracking-[0.1em] whitespace-nowrap" style={{ color: toneColor[tone] }}>
            A:{project.attentionScore}
            {project.lifecycle === "PAUSED" ? " · PAUSED" : ""}
          </span>
        </button>
      ))}
    </div>
  );
}
